import type { DateInvite, DateLength, Slot } from '@/api/types';

// v6.2 proposed times (SPEC_V6_2 §1). Mirrors the server rules so people
// learn before they submit: 1–5 times, each ≥ 30 min ahead, ≤ 30 days ahead.
export const MAX_SLOTS = 5;
export const MAX_SUGGESTED = 2;
export const SLOT_MIN_LEAD_MS = 30 * 60_000;
export const SLOT_MAX_AHEAD_MS = 30 * 86_400_000;

/** `<input type="datetime-local">` value for a Date (local time, minutes). */
export function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function slotTimeError(d: Date, now = new Date()): string | null {
  if (Number.isNaN(d.getTime())) return 'Pick a date and time.';
  if (d.getTime() < now.getTime() + SLOT_MIN_LEAD_MS) return 'At least 30 minutes from now.';
  if (d.getTime() > now.getTime() + SLOT_MAX_AHEAD_MS) return 'Within the next 30 days.';
  return null;
}

/** Minute precision, like the server. */
export function roundMinute(d: Date): Date {
  const x = new Date(d);
  x.setSeconds(0, 0);
  return x;
}

export interface QuickChip {
  id: string;
  label: string;
  at: Date;
}

function at(base: Date, addDays: number, hour: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + addDays);
  d.setHours(hour, 0, 0, 0);
  return d;
}

/**
 * Quick chips: Tonight 9pm (if still ≥ 30 min away), Tomorrow 8pm, the next
 * Sat 7pm and Sun 6pm — local time, de-duplicated, in order.
 */
export function quickChips(now = new Date()): QuickChip[] {
  const out: QuickChip[] = [];
  const tonight = at(now, 0, 21);
  if (tonight.getTime() >= now.getTime() + SLOT_MIN_LEAD_MS) out.push({ id: 'tonight', label: 'Tonight 9pm', at: tonight });
  out.push({ id: 'tomorrow', label: 'Tomorrow 8pm', at: at(now, 1, 20) });
  /** The next `dow` (0 = Sun) at `hour` that is still bookable. */
  const next = (dow: number, hour: number) => {
    const d = at(now, (dow - now.getDay() + 7) % 7, hour);
    return slotTimeError(d, now) ? at(d, 7, hour) : d;
  };
  for (const c of [
    { id: 'sat', label: 'Sat 7pm', at: next(6, 19) },
    { id: 'sun', label: 'Sun 6pm', at: next(0, 18) },
  ]) {
    if (!out.some((o) => o.at.getTime() === c.at.getTime()) && !slotTimeError(c.at, now)) out.push(c);
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** "Fri 17 Oct · 8:30 PM" in the viewer's local time. */
export function slotLabel(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }),
    time: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
}

export const hostSlots = (inv: Pick<DateInvite, 'slots'>): Slot[] => (inv.slots ?? []).filter((s) => s.by === 'host');
export const guestSlots = (inv: Pick<DateInvite, 'slots'>): Slot[] => (inv.slots ?? []).filter((s) => s.by === 'guest');

/**
 * The times the host can confirm (SPEC_V6_2 §1 "overlaps"): host slots the
 * guest said work, then the guest's own suggestions. Only future times.
 */
export function confirmable(inv: DateInvite, now = Date.now()): { slot: Slot; kind: 'works' | 'suggested' }[] {
  const ok = new Set(inv.planner?.theirs?.slots_ok ?? []);
  const out: { slot: Slot; kind: 'works' | 'suggested' }[] = [];
  for (const s of inv.slots ?? []) {
    if (Date.parse(s.starts_at) <= now) continue;
    if (s.by === 'host' && ok.has(s.id)) out.push({ slot: s, kind: 'works' });
    if (s.by === 'guest') out.push({ slot: s, kind: 'suggested' });
  }
  return out.sort((a, b) => Date.parse(a.slot.starts_at) - Date.parse(b.slot.starts_at));
}

/** The confirmed slot of a scheduled invite. */
export function confirmedSlot(inv: DateInvite): Slot | null {
  return (inv.slots ?? []).find((s) => s.id === inv.confirmed_slot) ?? null;
}

export function slotInputs(times: Date[], length: DateLength) {
  return times.map((t) => ({ starts_at: roundMinute(t).toISOString(), length_min: length }));
}
